import React from 'react';
import { Tag } from 'src/@vymo/ui/atoms';
import { createSelector } from '@reduxjs/toolkit';
import { getRevyreConfig } from 'src/models/lmsConfig/selectors';
import { RootState } from 'src/store';
import { STATE_ID } from './constants';
import { DomainType, WorkflowType } from './types';

export const getRevyreState = (state: RootState) => state?.revyre;

export const getIsLoading = (state: RootState) =>
  getRevyreState(state)?.uiState?.loaderCount > 0;

export const getNotification = (state: RootState) =>
  getRevyreState(state)?.uiState?.notification || { error: '', id: '' };

export const getDomainType = (state: RootState) =>
  getRevyreState(state).domainType;

export const getCurrentWorkflowType = (state: RootState) =>
  getRevyreState(state).workflowType;

export const getDomainConfigByType = (
  state: RootState,
  domainType,
  workflowType,
) =>
  domainType !== 'workflow'
    ? getRevyreState(state)?.modules?.[domainType]
    : getRevyreState(state)?.modules?.[workflowType];

export const getCurrentWorkflowConfig = createSelector(
  [(state) => state.revyre.modules, getCurrentWorkflowType],
  (domainConfig, workflowType) =>
    workflowType ? domainConfig?.[workflowType] : null,
);

export const getEntityList = createSelector(
  [(state: RootState) => state?.revyre?.list?.results || []],
  (results) => results.map((result) => result?.mutatedEntity),
);

export const getEntityListMetaData = (state: RootState) =>
  getRevyreState(state).list?.metaData;

export const getColumns = (state: RootState) =>
  getRevyreState(state).list?.columns;

export const getColumnFields = (
  state: RootState,
  domainType: DomainType,
  workflowType: WorkflowType,
) =>
  getDomainConfigByType(state, domainType, workflowType)?.defaultFields ?? [];

export const getFeatureConfigByType = (
  state: RootState,
  workflowType: WorkflowType,
) => getRevyreState(state)?.modules?.[workflowType]?.featuresConfig || {};

export const getListColumns = createSelector(
  [
    (state: RootState, domainType: DomainType) => domainType,
    (state: RootState, _domainType: DomainType, workflowType: WorkflowType) =>
      workflowType,
    (state: RootState, domainType: DomainType, workflowType: WorkflowType) =>
      getColumnFields(state, domainType, workflowType),
    getRevyreConfig,
  ],
  (domainType, workflowType, columns, revyreConfig) =>
    columns
      .filter(({ visible }) => (visible?.enabledFor || []).includes('web'))
      .map((column) =>
        column.code === STATE_ID
          ? {
              ...column,
              cell: (value: string) => {
                const states =
                  revyreConfig?.[workflowType || domainType]?.states || [];

                const correspondingState = states.find(
                  (event) => event?.id === value,
                );

                return <Tag label={correspondingState?.name} />;
              },
            }
          : column,
      ),
);

export const getListFilterFields = createSelector(
  [
    (state: RootState, domainType: DomainType, workflowType: WorkflowType) =>
      getColumnFields(state, domainType, workflowType),
  ],
  (columns) => {
    const filterFields = columns
      .filter(
        ({ filter }) => filter && (filter?.enabledFor ?? [])?.includes('web'),
      )
      .map((columnConfig) => columnConfig?.input);

    return filterFields;
  },
);

export const getCreateEntityFabName = (
  state: RootState,
  domainType: DomainType,
  workflowType: WorkflowType,
) => getDomainConfigByType(state, domainType, workflowType)?.fabName || 'Add';

export const getIsDialogOpen = (state: RootState) =>
  getRevyreState(state).uiState?.isDialogOpen;
